import { useCallback, useRef, useState } from "react";
import axios from "axios";
import {
    Upload,
    X,
    FileText,
    Image as ImageIcon,
    Video,
    Loader2,
    Paperclip,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICONS = {
    image: ImageIcon,
    pdf: FileText,
    video: Video,
    other: FileText,
};

const ACCEPT =
    "image/png,image/jpeg,image/webp,image/gif,application/pdf,video/mp4,video/quicktime,video/webm";

export default function ReferenceUploader({ files, setFiles, disabled }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const handleFiles = useCallback(
        async (fileList) => {
            if (!fileList || fileList.length === 0) return;
            setUploadError(null);
            setUploading(true);
            const newlyAdded = [];
            for (const f of Array.from(fileList)) {
                try {
                    const form = new FormData();
                    form.append("file", f);
                    const res = await axios.post(
                        `${API}/pipeline/upload`,
                        form,
                        {
                            headers: { "Content-Type": "multipart/form-data" },
                        },
                    );
                    newlyAdded.push(res.data);
                } catch (e) {
                    const msg =
                        e?.response?.data?.detail ||
                        e.message ||
                        "Upload failed";
                    setUploadError(`${f.name}: ${msg}`);
                }
            }
            if (newlyAdded.length)
                setFiles((prev) => [...prev, ...newlyAdded]);
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        },
        [setFiles],
    );

    const removeFile = (id) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
    };

    return (
        <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--text-muted)]">
                    Reference Material
                </label>
                <span className="text-[10px] text-[color:var(--text-muted)] font-mono opacity-70">
                    images · pdf · video · 15 MB max
                </span>
            </div>

            <div
                data-testid="reference-dropzone"
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled) setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => !disabled && inputRef.current?.click()}
                className={`cursor-pointer rounded-md border-2 border-dashed transition-colors duration-200 px-4 py-4 flex items-center gap-3 ${
                    disabled
                        ? "opacity-50 cursor-not-allowed border-[color:var(--border-subtle)] bg-[color:var(--surface-hover)]"
                        : dragging
                          ? "border-[color:var(--profecia-gold)] bg-[color:var(--profecia-gold-light)]"
                          : "border-[color:var(--border-subtle)] hover:border-[color:var(--profecia-gold-subtle)] bg-[color:var(--surface-hover)] hover:bg-white"
                }`}
            >
                {uploading ? (
                    <Loader2
                        size={16}
                        className="animate-spin text-[color:var(--profecia-gold)]"
                    />
                ) : (
                    <Upload
                        size={16}
                        className="text-[color:var(--profecia-gold)]"
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-sm text-[color:var(--text-main)] font-light">
                        {uploading
                            ? "Uploading…"
                            : dragging
                              ? "Drop files to attach"
                              : "Drop reference material or click to browse"}
                    </div>
                    <div className="text-[11px] text-[color:var(--text-muted)] mt-0.5">
                        Images stream to the LLM as visual references · PDFs
                        are text-extracted · videos noted as directional cues
                    </div>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    className="hidden"
                    data-testid="reference-file-input"
                    disabled={disabled}
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </div>

            {uploadError && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-1.5">
                    {uploadError}
                </div>
            )}

            {files.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="reference-file-list">
                    {files.map((f) => {
                        const Icon = KIND_ICONS[f.kind] || Paperclip;
                        return (
                            <li
                                key={f.id}
                                data-testid={`reference-file-${f.id}`}
                                className="flex items-center gap-3 px-3 py-2 bg-white border border-[color:var(--border-subtle)] rounded-md text-sm"
                            >
                                <Icon
                                    size={14}
                                    className="text-[color:var(--profecia-gold)] shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[color:var(--text-main)] truncate font-light">
                                        {f.filename}
                                    </div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                                        {f.kind} ·{" "}
                                        {(f.size_bytes / 1024).toFixed(0)} KB
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(f.id);
                                    }}
                                    disabled={disabled}
                                    data-testid={`remove-file-${f.id}`}
                                    className="p-1 text-[color:var(--text-muted)] hover:text-red-600 disabled:opacity-40 transition-colors"
                                    aria-label={`Remove ${f.filename}`}
                                >
                                    <X size={14} />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
